import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import InterventionTab from '../InterventionTab';
import SimulationOutput from './SimulationOutput';
import { api } from '../../api/client';
import type { PremortemResponse } from '../../types';
import Code from '../Code';

const PRESETS = [
  'Plan failed because Pure Leaf won the functional-tea segment by Q3 2027',
  'Plan failed because Vobev capacity disruption cut hero-can supply for 2 quarters',
  'Plan failed because a 7-Eleven private-label launch displaced Big Can endcap share',
  'Plan failed because aluminium repriced > 30% and we passed cost through',
];

export default function PreMortemConsole() {
  const [statement, setStatement] = useState(PRESETS[0]);
  const [horizon, setHorizon] = useState(4);
  const [topK, setTopK] = useState(3);

  const run = useMutation({
    mutationFn: () => api.preMortemRun(statement, horizon, topK),
  });

  return (
    <InterventionTab
      objective="O1 + O2 · surface unnamed risks by tracing backward from a stated failure"
      workflow={[
        'State a specific failure ("plan failed because [actor] won [segment]")',
        'Backward causal-chain generation (GenAI edge OR deterministic fallback)',
        'Deterministic scorer ranks chains by current-signal evidence support',
        'Output top-k chains + cheapest pre-emptive move to break each',
      ]}
      decisionRules={{
        rules: [
          'Trigger before major strategic commitments',
          'Human gate before any chain becomes a tracked risk',
          'Escalate to spine refresh if new risks emerge',
        ],
      }}
      aiLogic={{
        description: 'Hybrid: Gemini generates candidate chains (when GEMINI_API_KEY is configured); deterministic template fallback runs otherwise. Both paths go through the same deterministic evidence scorer. This is the single GenAI edge in the simulation stack.',
        modality: 'genai',
        hitlLevel: 'L4 human decision',
      }}
      kpi={{
        primary: 'Top-k highest-evidence failure chains + pre-emptive moves',
        downstream: 'Feeds tracked-risk register only after human gate',
        owner: 'Strategy · cross-functional review',
      }}
      content={
        <div className="panel">
          <div className="qhead">
            <span className="qdot" style={{ background: 'var(--blue)' }} />
            Failure statement · trace backward
          </div>

          <div style={{ marginBottom: 12 }}>
            <div className="small faint" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>Failure statement</div>
            <textarea
              className="c2-input"
              style={{ width: '100%', minHeight: 56, fontFamily: 'inherit', fontSize: 14, padding: 8, resize: 'vertical' }}
              value={statement}
              onChange={(e) => setStatement(e.target.value)}
            />
            <div className="row" style={{ gap: 6, marginTop: 6, flexWrap: 'wrap' }}>
              <span className="small faint" style={{ fontSize: 12 }}>Presets:</span>
              {PRESETS.map((p, i) => (
                <button key={i} type="button" className="btn ghost" style={{ fontSize: 11, padding: '3px 8px' }} onClick={() => setStatement(p)}>
                  {p.length > 40 ? `${p.slice(12, 50)}…` : p}
                </button>
              ))}
            </div>
          </div>

          <div className="row" style={{ gap: 14, marginBottom: 14, alignItems: 'flex-end' }}>
            <div>
              <div className="small faint" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>Horizon (quarters)</div>
              <input className="c2-input" type="number" min={1} max={12} value={horizon} onChange={(e) => setHorizon(parseInt(e.target.value, 10))} style={{ width: 100 }} />
            </div>
            <div>
              <div className="small faint" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>Top-k chains</div>
              <input className="c2-input" type="number" min={1} max={5} value={topK} onChange={(e) => setTopK(parseInt(e.target.value, 10))} style={{ width: 80 }} />
            </div>
            <button type="button" className="btn primary" onClick={() => run.mutate()} disabled={run.isPending}>
              {run.isPending ? 'Tracing chains…' : '⚡ Run Pre-Mortem'}
            </button>
          </div>

          {run.data && <PreMortemResult data={run.data} statement={statement} />}
        </div>
      }
    />
  );
}

function PreMortemResult({ data, statement }: { data: PremortemResponse; statement: string }) {
  return (
    <>
      <div className="row" style={{ gap: 10, marginBottom: 14, alignItems: 'center' }}>
        <span className={`tag ${data.gemini_status === 'used' ? 'amber' : 'muted'}`}>
          {data.gemini_status === 'used' ? 'Gemini-generated' : 'Deterministic fallback'}
        </span>
        <span className="small faint">
          {data.gemini_status === 'used'
            ? 'Live Gemini call · scorer is deterministic'
            : 'No GEMINI_API_KEY configured — templates from premortem_templates_v1.json'}
        </span>
      </div>

      {data.chains.map((c, i) => (
        <div key={c.id} className="panel" style={{ marginBottom: 10, background: i === 0 ? 'rgba(214,162,58,0.08)' : 'var(--panel2)', borderLeft: `3px solid ${i === 0 ? 'var(--amber)' : 'var(--line)'}` }}>
          <div className="row" style={{ justifyContent: 'space-between', marginBottom: 6 }}>
            <div className="row" style={{ gap: 8 }}>
              <span className={`tag ${i === 0 ? 'amber' : 'muted'}`} style={{ fontSize: 11 }}>#{i + 1}</span>
              <span className="mono small faint">{c.id}</span>
            </div>
            <div className="row" style={{ gap: 12 }}>
              <span className="small">evidence <b className="mono" style={{ color: c.evidence_score > 0.5 ? 'var(--olive)' : c.evidence_score > 0.3 ? 'var(--gold)' : 'var(--muted)' }}>{c.evidence_score.toFixed(2)}</b></span>
              <span className="small">prevent <b className="mono" style={{ color: 'var(--ink)' }}>${c.cost_to_prevent_usd_M.toFixed(1)}M</b></span>
            </div>
          </div>
          <div style={{ marginBottom: 6 }}>
            {c.chain_steps.map((step, j) => (
              <div key={j} className="row" style={{ alignItems: 'flex-start', gap: 8, fontSize: 13, lineHeight: 1.4, marginBottom: 2 }}>
                <span className="mono faint" style={{ fontSize: 11, width: 22, flexShrink: 0 }}>{j + 1}.</span>
                <span>{step}</span>
              </div>
            ))}
          </div>
          <div className="small" style={{ color: 'var(--muted)' }}>
            Supporting signals: {c.supporting_signal_ids.map((s) => (
              <span key={s} className="tag muted" style={{ fontSize: 11, marginRight: 4 }}><Code id={s} kind="signal" compact /></span>
            ))}
          </div>
        </div>
      ))}

      <div className="panel" style={{ background: 'var(--panel2)', marginBottom: 14 }}>
        <div className="small faint" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>
          Pre-emptive moves · one per chain
        </div>
        {data.pre_emptive_moves.map((m, i) => (
          <div key={i} className="row" style={{ justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid var(--line-soft)', fontSize: 13 }}>
            <div style={{ flex: 1 }}>
              <span className="mono small faint" style={{ marginRight: 8 }}>{m.mitigation_type}</span>
              {m.description}
            </div>
            <div className="row" style={{ gap: 14 }}>
              <span className="mono small">{m.owner}</span>
              <span className="mono small" style={{ color: 'var(--ink)' }}>${m.cost_to_prevent_usd_M.toFixed(1)}M</span>
            </div>
          </div>
        ))}
      </div>

      {/* 4-part Move Brief */}
      <div style={{ marginTop: 14 }}>
        <PreMortemMoveBrief data={data} statement={statement} />
      </div>
    </>
  );
}

function PreMortemMoveBrief({ data, statement }: { data: PremortemResponse; statement: string }) {
  const decideBy = new Date(Date.now() + 14 * 86400 * 1000).toISOString().slice(0, 10);
  const topChain = data.chains[0];
  const topMove = data.pre_emptive_moves.find((m) => m.chain_id === topChain?.id) ?? data.pre_emptive_moves[0];
  const recommendation = topMove
    ? topMove.description
    : 'Insufficient evidence to recommend a pre-emptive move; widen the monitoring window.';
  const recommendationDetail = topChain
    ? `Highest-evidence chain "${topChain.id}" scores ${topChain.evidence_score.toFixed(2)} on the current signal registry. Cheapest break: $${topMove?.cost_to_prevent_usd_M.toFixed(1) ?? '0'}M.`
    : 'Run with a more specific failure statement or expand top_k.';

  return (
    <SimulationOutput
      onDispose={async (d, _driftId, decision) => {
        await api.simulationDisposition({
          simulation_kind: 'pre_mortem',
          signal_id: topChain?.supporting_signal_ids[0] ?? 'S-02',
          disposition: d,
          owner: topMove?.owner ?? 'Strategy',
          recommendation,
          predicted_outcome: {
            failure_statement: statement,
            top_chain_id: topChain?.id ?? null,
            top_evidence: topChain?.evidence_score ?? 0,
            top_mitigation: topMove?.mitigation_type ?? null,
            gemini_status: data.gemini_status,
          },
          decision,
        });
      }}
      recommendation={recommendation}
      recommendationDetail={recommendationDetail}
      evidence={{
        headline: `Top-${data.chains.length} causal chains scored against the current signal registry. ${data.monitored_indicators.length} indicators contribute evidence.`,
        metrics: [
          { label: 'Chains scored', value: data.chains.length.toString(), color: 'gold' },
          { label: 'Top evidence', value: topChain ? topChain.evidence_score.toFixed(2) : '—', color: (topChain && topChain.evidence_score > 0.5) ? 'olive' : 'gold' },
          { label: 'Indicators', value: data.monitored_indicators.length.toString(), color: 'gold' },
          { label: 'Generator', value: data.gemini_status === 'used' ? 'Gemini' : 'Deterministic', color: 'gold' },
        ],
        exposedAssumption: 'Bets on the failure-actor + segment framing being the right place to look. If the actual failure mode is different, the chains miss; re-run with alternative framings.',
      }}
      alternatives={data.chains.slice(1, 3).map((c, i) => ({
        rank: (i + 2) as 2 | 3,
        move: `Pursue chain "${c.id}"`,
        expected: `evidence ${c.evidence_score.toFixed(2)} · prevent $${c.cost_to_prevent_usd_M.toFixed(1)}M`,
        whyLower: 'Lower evidence score than the recommended chain',
      }))}
      blocked={[]}  // Pre-Mortem doesn't generate BLOCKED options — it's a risk surfacing tool, not a move generator
      actionPath={{
        owner: topMove?.owner ?? 'Strategy · cross-functional review',
        decideBy,
        tripwire: topChain ? `Any of [${topChain.supporting_signal_ids.join(', ')}] breaches its tripwire` : 'Quarterly strategic review',
        rollback: 'Mark chain as monitored-only; do not commit pre-emptive spend',
      }}
    />
  );
}
